import { useMemo, useState, useRef, Fragment } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../../hooks/useDateSort';
import { PointhomeFunnelParticipationData } from '../../../../types/pointhomeFunnel.types';
import TableActionsMenu from '../../../../components/common/TableActionsMenu';
import type { Dayjs } from 'dayjs';

type Props = {
  data: PointhomeFunnelParticipationData;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  selectedContentFilter?: string;
};

// 룰렛 타입 목록
const ROULETTE_COLUMNS = [
  '1룰렛',
  '3룰렛',
  '5룰렛',
  '7룰렛',
  '10룰렛',
  '15룰렛',
  '30룰렛',
  '50룰렛',
  '70룰렛',
  '럭키',
  '오늘의 럭키룰렛',
] as const;

// 캐시 적립 유저 수를 표시할 콘텐츠 목록
const CASH_USER_COUNT_DISPLAY_CONTENTS = ['황금티켓', '출석체크', '오퍼월'] as const;

// 티켓 타입 목록
const TICKET_TYPES = ['광고티켓', '영상티켓'] as const;

export default function PointhomeParticipationTable({
  data,
  selectedApp,
  startDate,
  endDate,
  selectedContentFilter = '',
}: Props) {
  // 한 달치 데이터를 보기 위해 충분한 페이지 크기 설정
  const rowsPerPage = 100;
  const [page, setPage] = useState(0);
  const tableRef = useRef<HTMLTableElement>(null);

  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(data || []);

  const formatNumber = (value: number) => Math.round(value).toLocaleString('ko-KR');
  const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');
  const formatPercentage = (value: number) => `${(value * 100).toFixed(2)}%`;

  // 필터 타입 판단
  const isRouletteFilter = selectedContentFilter === '룰렛';
  const isCashUserCountDisplay = CASH_USER_COUNT_DISPLAY_CONTENTS.includes(
    selectedContentFilter as (typeof CASH_USER_COUNT_DISPLAY_CONTENTS)[number],
  );
  const isTicketFilter = selectedContentFilter === '티켓';

  // 날짜별로 그룹화하여 중복 제거
  const uniqueDatesData = useMemo(() => {
    if (!isRouletteFilter && !isTicketFilter) {
      // 일반 필터는 그대로 사용
      return sortedData;
    }

    // 룰렛/티켓 필터: 날짜별로 그룹화하여 하나의 행으로 표시
    const dateMap = new Map<string, (typeof sortedData)[0]>();

    sortedData.forEach((item) => {
      const date = item.date;
      if (!dateMap.has(date)) {
        // 첫 번째 항목을 기준으로 날짜 행 생성
        dateMap.set(date, {
          ...item,
          // 참여 유저 수는 모든 content의 합산
          participatedUser: 0,
        });
      }
      // 참여 유저 수 합산 (같은 날짜의 모든 content 합산)
      const dateRow = dateMap.get(date);
      if (dateRow) {
        dateRow.participatedUser += item.participatedUser;
      }
    });

    return Array.from(dateMap.values());
  }, [sortedData, isRouletteFilter, isTicketFilter]);

  const paged = useMemo(
    () => uniqueDatesData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [uniqueDatesData, page],
  );

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '';
    return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  // 퍼센트 컬럼은 소수로 전달
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((item) => {
      // 참여율을 소수 형태로 (0.xx)
      const participationRate = item.dau > 0 ? item.participatedUser / item.dau : 0;
      return [
        item.date,
        formatNumberOnly(item.dau),
        formatNumberOnly(item.participatedUser),
        participationRate,
      ];
    });
  }, [sortedData]);

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu
          tableRef={tableRef}
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="참여도"
        />
      </Box>
      {/* 테이블 */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 2,
          width: isRouletteFilter ? 'auto' : isTicketFilter ? 'auto' : '600px',
          overflowX: isRouletteFilter || isTicketFilter ? 'auto' : 'visible',
        }}
      >
        <TableContainer sx={{ width: '100%' }}>
          <Table ref={tableRef}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                    날짜
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                  DAU
                </TableCell>
                {isTicketFilter ? (
                  <>
                    {/* 티켓 필터: 광고티켓, 영상티켓 각각에 대해 클릭 유저 수, 티켓 적립 유저 수 */}
                    {TICKET_TYPES.map((ticketType) => (
                      <Fragment key={ticketType}>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          클릭 유저 수 ({ticketType})
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          티켓 적립 유저 수 ({ticketType})
                        </TableCell>
                      </Fragment>
                    ))}
                    {/* 참여율 칼럼들을 맨 오른쪽에 배치 */}
                    {TICKET_TYPES.map((ticketType) => (
                      <TableCell
                        key={`rate-${ticketType}`}
                        sx={{ fontWeight: 'bold' }}
                        align="right"
                      >
                        참여율 ({ticketType})
                      </TableCell>
                    ))}
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      클릭 유저 수
                    </TableCell>
                    {isRouletteFilter ? (
                      // 룰렛 필터: 각 룰렛 타입별 캐시 적립 유저 수 칼럼
                      ROULETTE_COLUMNS.map((rouletteType) => (
                        <TableCell key={rouletteType} sx={{ fontWeight: 'bold' }} align="right">
                          {rouletteType}
                        </TableCell>
                      ))
                    ) : (
                      <>
                        {isCashUserCountDisplay && (
                          // 황금티켓, 출석체크, 오퍼월: 캐시 적립 유저 수 칼럼 추가
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">
                            캐시 적립 유저 수
                          </TableCell>
                        )}
                      </>
                    )}
                    {/* 참여율 칼럼을 맨 오른쪽에 배치 */}
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      참여율
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((item) => (
                <TableRow key={item.date}>
                  <TableCell>{item.date}</TableCell>
                  <TableCell align="right">{formatNumber(item.dau)}</TableCell>
                  {isTicketFilter ? (
                    <>
                      {/* 티켓 필터: 광고티켓, 영상티켓 각각에 대해 클릭 유저 수, 티켓 적립 유저 수 표시 */}
                      {TICKET_TYPES.map((ticketType) => {
                        // 티켓 타입에 맞는 데이터 찾기 (content가 '광고티켓' 또는 '영상티켓'인 경우)
                        const ticketData = data.find(
                          (d) => d.date === item.date && d.content === ticketType,
                        );
                        return (
                          <Fragment key={ticketType}>
                            <TableCell align="right">
                              {ticketData ? formatNumber(ticketData.participatedUser) : '0'}
                            </TableCell>
                            <TableCell align="right">
                              {ticketData?.ticketUserCount
                                ? formatNumber(ticketData.ticketUserCount)
                                : '0'}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                      {/* 참여율 (티켓 적립 유저 수 기준) */}
                      {TICKET_TYPES.map((ticketType) => {
                        const ticketData = data.find(
                          (d) => d.date === item.date && d.content === ticketType,
                        );
                        const ticketUserCount = ticketData?.ticketUserCount ?? 0;
                        return (
                          <TableCell key={`rate-${ticketType}`} align="right">
                            {ticketData && ticketData.dau > 0
                              ? formatPercentage(ticketUserCount / ticketData.dau)
                              : '0.00%'}
                          </TableCell>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <TableCell align="right">{formatNumber(item.participatedUser)}</TableCell>
                      {isRouletteFilter ? (
                        // 룰렛 필터: 각 룰렛 타입별 캐시 적립 유저 수 표시
                        ROULETTE_COLUMNS.map((rouletteType) => {
                          const rouletteData = data.find(
                            (d) => d.date === item.date && d.content === rouletteType,
                          );
                          return (
                            <TableCell key={rouletteType} align="right">
                              {rouletteData?.cashUserCount
                                ? formatNumber(rouletteData.cashUserCount)
                                : '0'}
                            </TableCell>
                          );
                        })
                      ) : (
                        <>
                          {isCashUserCountDisplay && (
                            // 황금티켓, 출석체크, 오퍼월: 캐시 적립 유저 수 표시
                            <TableCell align="right">
                              {item.cashUserCount ? formatNumber(item.cashUserCount) : '0'}
                            </TableCell>
                          )}
                        </>
                      )}
                      {/* 참여율을 맨 오른쪽에 배치 */}
                      <TableCell align="right">
                        {formatPercentage(item.dau > 0 ? item.participatedUser / item.dau : 0)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={uniqueDatesData.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </TableContainer>
      </Box>
    </Box>
  );
}
